import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

class BuildABoxComponent extends Component {
  /** @type {import('./global').SkioDynamicBox | null} */ #boxData = null;
  /** @type {Map<string, number>} */ #selectedItems = new Map();
  /** @type {'subscribe' | 'one_time'} */ #purchaseType = 'subscribe';
  /** @type {string | null} */ #selectedSellingPlanId = null;
  /** @type {'slider' | 'grid'} */ #viewMode = 'slider';
  /** @type {Array<object> | null} */ #pendingBoxItems = null;
  /** @type {HTMLElement | null} */ #upsellBackdrop = null;
  /** @type {((e: KeyboardEvent) => void) | null} */ #upsellKeyHandler = null;
  /** @type {Map<string, object>} */ #metafieldCache = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.dataset.view = this.#viewMode;
    this.#fetchBoxData();
  }

  async #fetchBoxData() {
    const id = this.dataset.productPlatformId;
    if (!id) return;

    try {
      const res = await fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productPlatformId: id }),
      });

      if (!res.ok) throw new Error(`Skio API error: ${res.status}`);

      this.#boxData = (await res.json()).DynamicBox;

      const plans = this.#getPlans();
      if (plans.length) {
        this.#selectedSellingPlanId = plans[0].id;
      } else {
        this.#purchaseType = 'one_time';
      }

      const descKey = (this.dataset.productDescriptionMetafield ?? '').trim();
      const allergensKey = (this.dataset.productAllergensMetafield ?? '').trim();
      if (descKey || allergensKey) {
        const slugs = [
          ...new Set((this.#boxData.selectableProductVariants ?? []).map((p) => p.productHandle ?? '').filter(Boolean)),
        ];
        const keys = [descKey, allergensKey].filter(Boolean);
        await this.#fetchAllMetafields(slugs, keys);
      }

      this.#render();
    } catch (err) {
      console.error('[build-a-box] fetch failed:', err);
      this.innerHTML = `<p class="bab-error" role="alert">Unable to load the box builder. Please refresh the page.</p>`;
    }
  }

  #render() {
    const box = this.#boxData;
    if (!box) return;

    const [min, max] = box.sizeRange ?? [1, 99];
    const plans = this.#getPlans();
    const hasSubscription = plans.length > 0;
    const selectedPlan = plans.find((p) => p.id === this.#selectedSellingPlanId) ?? plans[0];
    const currency = this.dataset.currency ?? 'USD';

    const formatPrice = (/** @type {number} */ dollars) =>
      new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(dollars);

    const showProgressBar = this.dataset.showProgressBar !== 'false';

    const tiers = Object.entries(box.percentDiscountTier ?? {})
      .map(([size, pct]) => ({ size: Number(size), pct: Number(pct) }))
      .sort((a, b) => a.size - b.size);

    const tierLabels = [
      this.dataset.tier1Label,
      this.dataset.tier2Label,
      this.dataset.tier3Label,
      this.dataset.tier4Label,
    ];

    const topTierLabels = [
      this.dataset.tier1TopLabel,
      this.dataset.tier2TopLabel,
      this.dataset.tier3TopLabel,
      this.dataset.tier4TopLabel,
    ];

    const tierMarkers = tiers
      .map(({}, i) => {
        const left = ((i + 0.5) / tiers.length) * 100;
        return `<span class="bab-progress__marker" style="left:${left}%" aria-hidden="true"></span>`;
      })
      .join('');

    const tierTopLabelRow = tiers.length
      ? `<div class="bab-progress__labels bab-progress__labels--top" aria-hidden="true">${tiers
          .map(({}, i) => `<span class="bab-progress__marker-label">${this.#escHtml(topTierLabels[i] || '')}</span>`)
          .join('')}</div>`
      : '';

    const tierLabelRow = tiers.length
      ? `<div class="bab-progress__labels" aria-hidden="true">${tiers
          .map(({ pct }, i) => {
            const label = tierLabels[i] || `${pct}% off`;
            return `<span class="bab-progress__marker-label">${this.#escHtml(label)}</span>`;
          })
          .join('')}</div>`
      : '';

    const staticDescription = this.dataset.productDescription ?? '';
    const staticAllergens = this.dataset.productAllergens ?? '';
    const descKey = (this.dataset.productDescriptionMetafield ?? '').trim();
    const allergensKey = (this.dataset.productAllergensMetafield ?? '').trim();

    // Flatten products → variants; use productImageSrc from parent when variant has none
    const productCards = (box.selectableProductVariants ?? [])
      .flatMap((product) =>
        (product.productVariants ?? []).map((variant) => {
          const metafields = this.#metafieldCache.get(product.productHandle ?? '') ?? {};
          const description =
            (descKey ? BuildABoxComponent.#metafieldValue(metafields, descKey) : '') || staticDescription;
          const allergens =
            (allergensKey ? BuildABoxComponent.#metafieldValue(metafields, allergensKey) : '') || staticAllergens;
          const numericId = String(variant.platformId).split('/').pop() ?? '';
          const imageSrc = product.productImageSrc || variant.imageSrc || '';
          const variantTitle = variant.productVariantTitle ?? '';
          const titleEscaped = this.#escHtml(product.productTitle ?? '');
          const subPrice = selectedPlan?.variantMapping[variant.platformId]?.adjustedPrice;
          const showSubPrice = hasSubscription && this.#purchaseType === 'subscribe' && subPrice;
          const priceHtml = variant.price
            ? `<span class="bab-card__price">${
                showSubPrice
                  ? `${formatPrice(subPrice)}&nbsp;<s class="bab-card__price-was">${formatPrice(variant.price)}</s>`
                  : formatPrice(variant.price)
              }</span>`
            : '';
          return `
          <div
            class="bab-card"
            data-variant-id="${numericId}"
            data-variant-gid="${this.#escHtml(variant.platformId)}"
            data-regular-price="${variant.price || ''}"
            data-selected="false"
            on:click="/cardClick"
          >
            <div class="bab-card__img-wrap">
              ${
                imageSrc
                  ? `<img src="${imageSrc}" alt="${titleEscaped}" loading="lazy" width="400" height="400">`
                  : `<div class="bab-card__img-placeholder" aria-hidden="true"></div>`
              }
              <span class="bab-card__checkbox" aria-hidden="true"></span>
            </div>
            <div class="bab-card__body">
              <span class="bab-card__title">${titleEscaped}</span>
              ${variantTitle && variantTitle !== 'Default Title' ? `<span class="bab-card__variant">${this.#escHtml(variantTitle)}</span>` : ''}
              ${priceHtml}
              ${description ? `<p class="bab-card__description">${this.#escHtml(description)}</p>` : ''}
              ${allergens ? `<p class="bab-card__allergens">${this.#escHtml(allergens)}</p>` : ''}
              <div class="bab-card__qty-row">
                <button class="bab-card__qty-btn" type="button" data-variant-id="${numericId}" data-action="dec" on:click="/decrementCard" aria-label="Remove ${titleEscaped}" disabled>−</button>
                <span class="bab-card__qty-value" aria-live="polite">0</span>
                <button class="bab-card__qty-btn bab-card__qty-btn--inc" type="button" data-variant-id="${numericId}" data-action="inc" on:click="/incrementCard" aria-label="Add ${titleEscaped}">+</button>
              </div>
            </div>
          </div>`;
        })
      )
      .join('');

    const frequencyOptions = plans
      .map((plan) => `<option value="${plan.id}">${this.#escHtml(plan.title)}</option>`)
      .join('');

    const subscribeBadgeText = this.dataset.subscribeBadgeText?.trim();
    const subscriptionOnly = this.dataset.subscriptionOnly === 'true';

    const subscribeTitle =
      this.dataset.subscribeTitle?.trim() ||
      `Subscribe &amp; Save${plans[0]?.priceAdjustmentAmount ? ` ${plans[0].priceAdjustmentAmount}%` : ''}`;

    let subscriptionUI = '';
    if (hasSubscription) {
      if (subscriptionOnly) {
        // Subscription-only mode: hide the one-time toggle entirely.
        // Show frequency selector only when the customer has more than one plan to choose from.
        subscriptionUI =
          plans.length > 1
            ? `<select class="bab-frequency-select" ref="frequencySelect" on:change="/setSellingPlan" aria-label="Delivery frequency">${frequencyOptions}</select>`
            : '';
      } else {
        subscriptionUI = `
          <div class="bab-purchase-options" role="group" aria-label="Purchase type">
            <label class="bab-purchase-option" data-option="subscribe">
              <input type="radio" name="bab-purchase-${box.boxId}" value="subscribe" on:change="/setPurchaseType" ${this.#purchaseType === 'subscribe' ? 'checked' : ''}>
              <div class="bab-purchase-option__row">
                <span class="bab-purchase-option__title">${subscribeTitle}</span>
                ${frequencyOptions ? `<select class="bab-frequency-select" ref="frequencySelect" on:change="/setSellingPlan" aria-label="Delivery frequency" ${this.#purchaseType !== 'subscribe' ? 'hidden' : ''}>${frequencyOptions}</select>` : ''}
              </div>
              ${subscribeBadgeText ? `<div class="bab-subscribe-info">${subscribeBadgeText}</div>` : ''}
            </label>
            <label class="bab-purchase-option" data-option="one_time">
              <input type="radio" name="bab-purchase-${box.boxId}" value="one_time" on:change="/setPurchaseType" ${this.#purchaseType === 'one_time' ? 'checked' : ''}>
              <span class="bab-purchase-option__title">One-Time Purchase</span>
            </label>
          </div>`;
      }
    }

    this.innerHTML = `
      ${
        showProgressBar
          ? `
      <div class="bab-progress">
        ${tierTopLabelRow}
        <div class="bab-progress__track">
          <div
            class="bab-progress__fill"
            ref="progressFill"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="${max}"
            aria-valuenow="0"
            aria-label="Items selected"
            style="width:0%"
          ></div>
          ${tierMarkers}
        </div>
        ${tierLabelRow}
        <p class="bab-progress__status" ref="progressStatus">Select ${min} items to build your box</p>
      </div>`
          : ''
      }
      <div class="bab-grid-header">
        <div class="bab-view-toggle" role="group" aria-label="View mode">
          <label class="bab-view-toggle__label">
            <input type="radio" name="bab-view-${box.boxId}" value="slider" on:change="/setViewMode" ${this.#viewMode === 'slider' ? 'checked' : ''}>
            Slider
          </label>
          <label class="bab-view-toggle__label">
            <input type="radio" name="bab-view-${box.boxId}" value="grid" on:change="/setViewMode" ${this.#viewMode === 'grid' ? 'checked' : ''}>
            Grid
          </label>
        </div>
        <div class="bab-scroll-track">
          <div class="bab-scroll-thumb" ref="scrollThumb"></div>
        </div>
      </div>
      <div class="bab-slider-wrap">
        <div class="bab-grid" ref="grid" aria-label="Available products">
          ${productCards}
        </div>
        <div class="bab-slider-controls">
          <button class="bab-slider-btn bab-slider-btn--prev" type="button" on:click="/prevSlide" aria-label="Previous products">&#8249;</button>
          <button class="bab-slider-btn bab-slider-btn--next" type="button" on:click="/nextSlide" aria-label="Next products">&#8250;</button>
        </div>
      </div>
      <div class="bab-controls">
        <div class="bab-summary-row">
          <p class="bab-counter" ref="counter" role="status">0 of ${max} selected</p>
          <span class="bab-discount-badge" ref="discountBadge"></span>
        </div>
        ${subscriptionUI}
        <p class="bab-error" ref="errorMessage" role="alert" hidden></p>
        <button
          class="bab-add-btn"
          ref="addToCartBtn"
          type="button"
          on:click="/handleAddToCart"
          disabled
          aria-disabled="true"
        >
          <span class="bab-add-btn__label">${this.#escHtml(this.dataset.addToBoxText || 'Add to box')}</span>
          <span class="bab-add-btn__price" ref="btnPrice" hidden></span>
        </button>
      </div>`;
    this.#setupScrollTrack();
  }

  /** @param {Event} event */
  setViewMode(event) {
    this.#viewMode = /** @type {'slider' | 'grid'} */ (/** @type {HTMLInputElement} */ (event.target).value);
    this.dataset.view = this.#viewMode;
    if (this.#viewMode === 'slider') this.#updateScrollThumb();
  }

  #setupScrollTrack() {
    const grid = this.querySelector('.bab-grid');
    if (!grid) return;
    this.#updateScrollThumb();
    grid.addEventListener('scroll', () => this.#updateScrollThumb(), { passive: true });
  }

  #updateScrollThumb() {
    const grid = this.querySelector('.bab-grid');
    const thumb = /** @type {HTMLElement | null} */ (this.querySelector('[ref="scrollThumb"]'));
    if (!grid || !thumb) return;
    const { scrollLeft, scrollWidth, clientWidth } = grid;
    const thumbPct = (clientWidth / scrollWidth) * 100;
    const leftPct = scrollWidth > clientWidth ? (scrollLeft / (scrollWidth - clientWidth)) * (100 - thumbPct) : 0;
    thumb.style.width = `${thumbPct}%`;
    thumb.style.left = `${leftPct}%`;
  }

  prevSlide() {
    const grid = this.querySelector('.bab-grid');
    if (!grid) return;
    grid.scrollBy({ left: -grid.clientWidth, behavior: 'smooth' });
  }

  nextSlide() {
    const grid = this.querySelector('.bab-grid');
    if (!grid) return;
    grid.scrollBy({ left: grid.clientWidth, behavior: 'smooth' });
  }

  /** @param {MouseEvent} event */
  cardClick(event) {
    if (/** @type {HTMLElement} */ (event.target).closest('.bab-card__qty-btn')) return;

    const card = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (event.target).closest('.bab-card'));
    if (!card || card.dataset.selected === 'true') return;

    const variantId = card.dataset.variantId ?? '';
    if (!variantId) return;

    const [, max] = this.#boxData?.sizeRange ?? [1, 99];
    const total = [...this.#selectedItems.values()].reduce((s, q) => s + q, 0);
    if (total >= max) return;

    this.#selectedItems.set(variantId, 1);
    this.#updateCard(variantId);
    this.#updateSummary();
  }

  /** @param {MouseEvent} event */
  incrementCard(event) {
    const btn = /** @type {HTMLElement} */ (event.target).closest('[data-action="inc"]');
    const variantId = /** @type {HTMLElement | null} */ (btn)?.dataset.variantId ?? '';
    if (!variantId) return;

    const [, max] = this.#boxData?.sizeRange ?? [1, 99];
    const total = [...this.#selectedItems.values()].reduce((s, q) => s + q, 0);
    if (total >= max) return;

    this.#selectedItems.set(variantId, (this.#selectedItems.get(variantId) ?? 0) + 1);
    this.#updateCard(variantId);
    this.#updateSummary();
  }

  /** @param {MouseEvent} event */
  decrementCard(event) {
    const btn = /** @type {HTMLElement} */ (event.target).closest('[data-action="dec"]');
    const variantId = /** @type {HTMLElement | null} */ (btn)?.dataset.variantId ?? '';
    if (!variantId) return;

    const qty = this.#selectedItems.get(variantId) ?? 0;
    if (qty <= 1) {
      this.#selectedItems.delete(variantId);
    } else {
      this.#selectedItems.set(variantId, qty - 1);
    }
    this.#updateCard(variantId);
    this.#updateSummary();
  }

  /** @param {string} variantId */
  #updateCard(variantId) {
    const card = this.querySelector(`.bab-card[data-variant-id="${variantId}"]`);
    if (!card) return;

    const qty = this.#selectedItems.get(variantId) ?? 0;
    card.setAttribute('data-selected', String(qty > 0));

    const qtyValue = card.querySelector('.bab-card__qty-value');
    if (qtyValue) qtyValue.textContent = String(qty);

    const decBtn = card.querySelector('[data-action="dec"]');
    if (decBtn) decBtn.toggleAttribute('disabled', qty <= 0);
  }

  /** @param {Event} event */
  setPurchaseType(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    this.#purchaseType = input.value === 'subscribe' ? 'subscribe' : 'one_time';

    const freqSelect = this.querySelector('[ref="frequencySelect"]');
    if (freqSelect) freqSelect.toggleAttribute('hidden', this.#purchaseType !== 'subscribe');

    this.#updateCardPrices();
    this.#updateSubtotalDisplay();
  }

  /** @param {Event} event */
  setSellingPlan(event) {
    this.#selectedSellingPlanId = /** @type {HTMLSelectElement} */ (event.target).value;
    this.#updateCardPrices();
    this.#updateSubtotalDisplay();
  }

  #buildBoxItems() {
    const box = this.#boxData;
    if (!box) return null;

    const [min] = box.sizeRange ?? [1];
    const total = [...this.#selectedItems.values()].reduce((s, q) => s + q, 0);
    if (total < min) return null;

    return [...this.#selectedItems.entries()].flatMap(([variantId, qty]) =>
      Array.from({ length: qty }, () => ({
        id: Number(variantId),
        quantity: 1,
        ...(this.#purchaseType === 'subscribe' && this.#selectedSellingPlanId
          ? { selling_plan: Number(this.#selectedSellingPlanId) }
          : {}),
        properties: { _dynamicBoxId: box.boxId, _dynamicBoxIndex: 0 },
      }))
    );
  }

  async #submitToCart(items) {
    const btn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="addToCartBtn"]'));
    if (btn) btn.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({ items }),
        })
      );
      const data = await res.json();

      if (data.status) {
        this.#showError(data.message ?? 'Unable to add items to cart.');
        return;
      }

      document.dispatchEvent(
        new CartAddEvent(data, String(items[0].id), {
          source: 'build-a-box-component',
          itemCount: items.length,
        })
      );
    } catch {
      this.#showError('Something went wrong. Please try again.');
    } finally {
      if (btn) btn.removeAttribute('aria-busy');
    }
  }

  async handleAddToCart() {
    const items = this.#buildBoxItems();
    if (!items) return;

    const upsells = this.#getUpsellProducts();
    if (this.dataset.enableUpsell === 'true' && upsells.length > 0) {
      this.#pendingBoxItems = items;
      this.#openUpsellModal(upsells);
      return;
    }

    await this.#submitToCart(items);
  }

  /**
   * Normalizes API upsell arrays into a common shape.
   * `sellingPlanId` is null for one-time upsells and a numeric ID for recurring ones.
   * Future: add ...normalize(box.recurringUpsells, box.recurringUpsellsSellingPlanMapping)
   * @returns {Array<{id: number, title: string, price: number, image: string, sellingPlanId: number | null}>}
   */
  #getUpsellProducts() {
    const box = this.#boxData;
    if (!box) return [];

    const normalize = (products, sellingPlanMapping = {}) =>
      (products ?? []).flatMap((product) => {
        const variant = product.productVariants?.[0];
        if (!variant) return [];
        const numericId = Number(String(variant.platformId).split('/').pop());
        const planGid = sellingPlanMapping[variant.platformId]?.planId ?? null;
        const sellingPlanId = planGid ? Number(planGid.split('/').pop()) : null;
        return [
          {
            id: numericId,
            title: product.productTitle ?? '',
            price: variant.price ?? 0,
            image: product.productImageSrc || variant.imageSrc || '',
            sellingPlanId,
          },
        ];
      });

    return [
      ...normalize(box.oneTimeUpsells, box.oneTimeUpsellsSellingPlanMapping ?? {}),
      // Future recurring upsells: ...normalize(box.recurringUpsells, box.recurringUpsellsSellingPlanMapping ?? {}),
    ];
  }

  /** @param {Array<{id: number, title: string, price: number, image: string, sellingPlanId: number | null}>} upsells */
  #openUpsellModal(upsells) {
    const heading = this.#escHtml(this.dataset.upsellHeading || 'Complete your order');
    const acceptText = this.#escHtml(this.dataset.upsellAcceptText || 'Add to my order');
    const declineText = this.#escHtml(this.dataset.upsellDeclineText || 'No thanks, continue');
    const upsellBody = this.dataset.upsellBody ?? '';
    const modalId = `bab-upsell-heading-${this.dataset.productPlatformId}`;

    const backdrop = document.createElement('div');
    backdrop.className = 'bab-upsell-backdrop';
    backdrop.innerHTML = `
      <div class="bab-upsell-modal" role="dialog" aria-modal="true" aria-labelledby="${modalId}">
        <button class="bab-upsell-close" type="button" aria-label="Close">&#x2715;</button>
        <h2 class="bab-upsell-heading" id="${modalId}">${heading}</h2>
        ${upsellBody ? `<div class="bab-upsell-body">${upsellBody}</div>` : ''}
        <div class="bab-upsell-products">
          ${upsells
            .map(
              (p) => `
            <label class="bab-upsell-product">
              <input type="checkbox" class="bab-upsell-checkbox" value="${p.id}" data-selling-plan-id="${p.sellingPlanId ?? ''}">
              ${p.image ? `<img class="bab-upsell-product__img" src="${this.#escHtml(p.image)}" alt="${this.#escHtml(p.title)}" loading="lazy" width="200" height="200">` : ''}
              <div class="bab-upsell-product__info">
                <span class="bab-upsell-product__title">${this.#escHtml(p.title)}</span>
                <span class="bab-upsell-product__price">${this.#formatPrice(p.price)}</span>
              </div>
            </label>`
            )
            .join('')}
        </div>
        <div class="bab-upsell-actions">
          <button class="bab-upsell-accept" type="button">${acceptText}</button>
          <button class="bab-upsell-decline" type="button">${declineText}</button>
        </div>
      </div>`;

    document.body.appendChild(backdrop);
    document.body.style.overflow = 'hidden';
    this.#upsellBackdrop = backdrop;

    this.#upsellKeyHandler = (e) => {
      if (e.key === 'Escape') this.#onUpsellDecline();
    };
    document.addEventListener('keydown', this.#upsellKeyHandler);

    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) this.#onUpsellDecline();
    });
    backdrop.querySelector('.bab-upsell-close')?.addEventListener('click', () => this.#onUpsellDecline());
    backdrop.querySelector('.bab-upsell-accept')?.addEventListener('click', () => this.#onUpsellAccept());
    backdrop.querySelector('.bab-upsell-decline')?.addEventListener('click', () => this.#onUpsellDecline());

    const focusables = /** @type {HTMLElement[]} */ (
      [...backdrop.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])')].filter(
        (el) => !el.hasAttribute('disabled')
      )
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    first?.focus();

    backdrop.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    });
  }

  #closeUpsellModal() {
    this.#upsellBackdrop?.remove();
    this.#upsellBackdrop = null;
    if (this.#upsellKeyHandler) {
      document.removeEventListener('keydown', this.#upsellKeyHandler);
      this.#upsellKeyHandler = null;
    }
    document.body.style.overflow = '';
    /** @type {HTMLElement | null} */ (this.querySelector('[ref="addToCartBtn"]'))?.focus();
  }

  async #onUpsellAccept() {
    const backdrop = this.#upsellBackdrop;
    const boxItems = this.#pendingBoxItems ?? [];
    const upsellItems = [...(backdrop?.querySelectorAll('.bab-upsell-checkbox:checked') ?? [])].map((cb) => {
      const input = /** @type {HTMLInputElement} */ (cb);
      const planId = input.dataset.sellingPlanId;
      return {
        id: Number(input.value),
        quantity: 1,
        ...(planId ? { selling_plan: Number(planId) } : {}),
      };
    });
    this.#pendingBoxItems = null;
    this.#closeUpsellModal();
    await this.#submitToCart([...boxItems, ...upsellItems]);
  }

  async #onUpsellDecline() {
    const boxItems = this.#pendingBoxItems ?? [];
    this.#pendingBoxItems = null;
    this.#closeUpsellModal();
    await this.#submitToCart(boxItems);
  }

  #updateSummary() {
    const box = this.#boxData;
    if (!box) return;

    const total = [...this.#selectedItems.values()].reduce((s, q) => s + q, 0);
    const [min, max] = box.sizeRange ?? [1, 99];

    const counter = this.querySelector('[ref="counter"]');
    if (counter) counter.textContent = `${total} of ${max} selected`;

    const btn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="addToCartBtn"]'));
    if (btn) {
      btn.disabled = total < min;
      btn.setAttribute('aria-disabled', String(total < min));
    }

    const badge = this.querySelector('[ref="discountBadge"]');
    if (badge) badge.textContent = this.#discountLabel(total);

    this.querySelectorAll('.bab-card__qty-btn--inc').forEach((incBtn) =>
      incBtn.toggleAttribute('disabled', total >= max)
    );

    this.#updateSubtotalDisplay();
    this.#updateProgressBar(total);
  }

  /** @param {number} total */
  #updateProgressBar(total) {
    const box = this.#boxData;
    if (!box) return;

    const [, max] = box.sizeRange ?? [1, 99];
    const tiers = Object.entries(box.percentDiscountTier ?? {})
      .map(([size, pct]) => ({ size: Number(size), pct: Number(pct) }))
      .sort((a, b) => a.size - b.size);

    const fill = this.querySelector('[ref="progressFill"]');
    if (fill) {
      const widthPct =
        tiers.length > 0 ? this.#tierNormalizedFill(total, tiers, max) : Math.min((total / max) * 100, 100);
      /** @type {HTMLElement} */ (fill).style.width = `${widthPct}%`;
      fill.setAttribute('aria-valuenow', String(total));
    }

    const status = this.querySelector('[ref="progressStatus"]');
    if (status) {
      const tierLabels = [
        this.dataset.tier1Label,
        this.dataset.tier2Label,
        this.dataset.tier3Label,
        this.dataset.tier4Label,
      ];

      const nextTierIdx = tiers.findIndex(({ size }) => total < size);
      const nextTier = nextTierIdx >= 0 ? tiers[nextTierIdx] : null;
      const currentTierIdx = tiers.reduce((acc, { size }, i) => (total >= size ? i : acc), -1);
      const currentTier = currentTierIdx >= 0 ? tiers[currentTierIdx] : null;

      const icon = this.dataset.progressIcon ? `${this.dataset.progressIcon} ` : '';
      const unlockedText = this.dataset.progressUnlockedText;

      if (nextTier) {
        const needed = nextTier.size - total;
        const label = tierLabels[nextTierIdx] || `${nextTier.pct}% off`;
        status.textContent = `${icon}Add ${needed} more for ${label}`;
      } else if (currentTier) {
        const label = tierLabels[currentTierIdx] || `${currentTier.pct}% off`;
        status.textContent = `${icon}${unlockedText || `${label} unlocked!`}`;
      } else {
        status.textContent = '';
      }
    }
  }

  /**
   * Maps item count to a fill % so the fill lands exactly on each dot (centered
   * under its label) when the tier threshold is reached, then continues to 100%
   * as items approach the box max.
   * @param {number} total
   * @param {{size: number, pct: number}[]} tiers sorted ascending by size
   * @param {number} max box max items
   */
  #tierNormalizedFill(total, tiers, max) {
    if (total <= 0) return 0;
    if (total >= max) return 100;
    const N = tiers.length;
    const segPct = 100 / N;
    // Breakpoints: [itemCount, fillPct] — fill aligns with dot center at each tier
    const bp = [[0, 0], ...tiers.map((t, i) => [t.size, (i + 0.5) * segPct]), [max, 100]];
    for (let i = 0; i < bp.length - 1; i++) {
      const [lo, loFill] = bp[i];
      const [hi, hiFill] = bp[i + 1];
      if (total >= lo && total <= hi) {
        return loFill + ((total - lo) / (hi - lo)) * (hiFill - loFill);
      }
    }
    return 100;
  }

  /** @param {number} dollars */
  #formatPrice(dollars) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.dataset.currency ?? 'USD',
      minimumFractionDigits: 2,
    }).format(dollars);
  }

  #calcSubtotal() {
    const box = this.#boxData;
    if (!box) return 0;

    const plans = this.#getPlans();
    const selectedPlan = plans.find((p) => p.id === this.#selectedSellingPlanId) ?? plans[0];
    const variantPrices = selectedPlan?.variantMapping ?? {};

    const priceMap = new Map();
    for (const product of box.selectableProductVariants ?? []) {
      for (const variant of product.productVariants ?? []) {
        const numericId = String(variant.platformId).split('/').pop() ?? '';
        priceMap.set(numericId, { regularPrice: variant.price, platformGid: variant.platformId });
      }
    }

    let subtotal = 0;
    for (const [variantId, qty] of this.#selectedItems) {
      const info = priceMap.get(variantId);
      if (!info) continue;
      const subPrice = variantPrices[info.platformGid]?.adjustedPrice;
      const unitPrice = this.#purchaseType === 'subscribe' && subPrice ? subPrice : info.regularPrice;
      subtotal += unitPrice * qty;
    }

    // Apply the volume tier discount on top of the per-unit prices
    const totalItems = [...this.#selectedItems.values()].reduce((s, q) => s + q, 0);
    const tierPct = Object.entries(box.percentDiscountTier ?? {})
      .map(([size, pct]) => ({ size: Number(size), pct: Number(pct) }))
      .filter(({ size }) => totalItems >= size)
      .reduce((best, { pct }) => Math.max(best, pct), 0);

    return subtotal * (1 - tierPct / 100);
  }

  #updateSubtotalDisplay() {
    const itemCount = [...this.#selectedItems.values()].reduce((s, q) => s + q, 0);
    const btnPrice = /** @type {HTMLElement | null} */ (this.querySelector('[ref="btnPrice"]'));
    if (!btnPrice) return;

    if (itemCount > 0) {
      const discounted = this.#calcSubtotal();
      const original = this.#calcOriginalSubtotal();
      const hasDiscount = original > discounted + 0.005;
      btnPrice.innerHTML = hasDiscount
        ? `${this.#formatPrice(discounted)}&ensp;<s class="bab-add-btn__original">${this.#formatPrice(original)}</s>`
        : this.#formatPrice(discounted);
      btnPrice.removeAttribute('hidden');
    } else {
      btnPrice.setAttribute('hidden', '');
    }
  }

  #calcOriginalSubtotal() {
    const box = this.#boxData;
    if (!box) return 0;

    const priceMap = new Map();
    for (const product of box.selectableProductVariants ?? []) {
      for (const variant of product.productVariants ?? []) {
        const numericId = String(variant.platformId).split('/').pop() ?? '';
        priceMap.set(numericId, variant.price);
      }
    }

    let total = 0;
    for (const [variantId, qty] of this.#selectedItems) {
      total += (priceMap.get(variantId) ?? 0) * qty;
    }
    return total;
  }

  #getPlans() {
    const group = this.#boxData?.sellingPlanGroup ?? {};
    return Object.entries(group)
      .filter(([, plan]) => plan && plan.title)
      .sort(([, a], [, b]) => (a.position ?? 0) - (b.position ?? 0))
      .map(([gid, plan]) => ({
        id: gid.split('/').pop() ?? gid,
        gid,
        title: /** @type {string} */ (plan.title),
        priceAdjustmentAmount: /** @type {number} */ (plan.priceAdjustmentAmount ?? 0),
        variantMapping: /** @type {Record<string, {adjustedPrice: number}>} */ (plan.variantMapping ?? {}),
      }));
  }

  #updateCardPrices() {
    const plans = this.#getPlans();
    const selectedPlan = plans.find((p) => p.id === this.#selectedSellingPlanId) ?? plans[0];
    const variantPrices = selectedPlan?.variantMapping ?? {};
    const currency = this.dataset.currency ?? 'USD';
    const fmt = (/** @type {number} */ n) =>
      new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(n);

    this.querySelectorAll('.bab-card').forEach((card) => {
      const el = /** @type {HTMLElement} */ (card);
      const regularPrice = Number(el.dataset.regularPrice);
      const variantGid = el.dataset.variantGid ?? '';
      const priceEl = card.querySelector('.bab-card__price');
      if (!priceEl || !regularPrice) return;

      const subPrice = variantPrices[variantGid]?.adjustedPrice;
      if (this.#purchaseType === 'subscribe' && subPrice) {
        priceEl.innerHTML = `${fmt(subPrice)}&nbsp;<s class="bab-card__price-was">${fmt(regularPrice)}</s>`;
      } else {
        priceEl.textContent = fmt(regularPrice);
      }
    });
  }

  /** @param {number} count */
  #discountLabel(count) {
    const tiers = Object.entries(this.#boxData?.percentDiscountTier ?? {})
      .filter(([size]) => count >= Number(size))
      .sort(([a], [b]) => Number(b) - Number(a));
    return tiers.length ? `${tiers[0][1]}% off` : '';
  }

  /** @param {string} message */
  #showError(message) {
    const el = this.querySelector('[ref="errorMessage"]');
    if (!el) return;
    el.textContent = message;
    el.removeAttribute('hidden');
    setTimeout(() => el?.setAttribute('hidden', ''), 8000);
  }

  /**
   * @param {object} data — flat map of "namespace.key" → value strings
   * @param {string} namespaceKey — e.g. "custom.short_description"
   */
  static #metafieldValue(data, namespaceKey) {
    if (!namespaceKey || !data) return '';
    return String(data[namespaceKey] ?? '');
  }

  /**
   * Fetches metafields for all slugs in a single batched Storefront API request.
   * @param {string[]} slugs
   * @param {string[]} keys — namespace.key strings, e.g. ["custom.short_description"]
   */
  async #fetchAllMetafields(slugs, keys) {
    const shopDomain = (this.dataset.shopDomain ?? '').trim();
    const token = (this.dataset.storefrontToken ?? '').trim();
    if (!shopDomain || !token || !slugs.length || !keys.length) return;

    const uncached = slugs.filter((s) => !this.#metafieldCache.has(s));
    if (!uncached.length) return;

    const identifiers = keys.map((k) => {
      const dot = k.indexOf('.');
      return { namespace: k.slice(0, dot), key: k.slice(dot + 1) };
    });
    const identifiersGql = identifiers
      .map((id) => `{namespace: ${JSON.stringify(id.namespace)}, key: ${JSON.stringify(id.key)}}`)
      .join(', ');
    const queryBody = uncached
      .map(
        (slug, i) =>
          `p${i}: product(handle: ${JSON.stringify(slug)}) { metafields(identifiers: [${identifiersGql}]) { namespace key value } }`
      )
      .join(' ');

    try {
      const res = await fetch(`https://${shopDomain}/api/2026-04/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: `{ ${queryBody} }` }),
      });
      const { data } = await res.json();
      uncached.forEach((slug, i) => {
        const flat = {};
        for (const mf of data?.[`p${i}`]?.metafields ?? []) {
          if (mf) flat[`${mf.namespace}.${mf.key}`] = mf.value;
        }
        this.#metafieldCache.set(slug, flat);
      });
    } catch {
      uncached.forEach((slug) => this.#metafieldCache.set(slug, {}));
    }
  }

  /** @param {string} str */
  #escHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

if (!customElements.get('build-a-box-component')) {
  customElements.define('build-a-box-component', BuildABoxComponent);
}
